import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getDetailEvent,
  postBoothBottleReviewSummary,
  postCheckBookedDate,
  postReviewSummary,
} from "../../services/apiService";
import BookingBottleSelection from "./BookingBottleSelection.jsx";
import {
  buildBottleCatalog,
  buildBottleSelectionPayload,
  buildReviewBottlePayload,
  getBottleServiceEventId,
  getDefaultReservationPrice,
  isBoothBottleServiceEnabled,
  toBottleEntries,
} from "../../utils/bottleServiceHelpers.js";
import { vibesColors as colors } from "../../theme/vibesColors";
import { isApiSuccess, isVipEntryEvent, pickFloorLayoutUrl } from "../../utils/clubDetailHelpers";
import {
  capacityForBookingDate,
  INVALID_BOOKING_RANGE_MESSAGE,
  isBookingDateUnavailable,
  readDateCapacityFromRow,
  resolveActiveBoothForBooking,
  resolveBookingMaxCapacity,
  pickEventEndDateYmd,
  pickEventStartDateYmd,
  pickFixedSingleEventDateYmd,
} from "../../utils/bookingHelpers";
import WebsiteLayout from "../../layouts/WebsiteLayout";
import LoadingSpinner from "../common/LoadingSpinner";
import BookingCard from "./BookingCard";
import BookingUserForm from "./BookingUserForm";
import BookingCalculation from "./BookingCalculation";
import "../../pages/BookingPage.css";

const DAY_NAMES = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

function pad2(v) {
  return String(v).padStart(2, "0");
}
function toYmd(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}
function toDmyShort(ymd) {
  const [y, m, d] = String(ymd || "").split("-");
  if (!y || !m || !d) return "";
  return `${d}-${m}-${String(y).slice(-2)}`;
}
function toMonthLabel(date) {
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}
function toShortDateLabel(ymd) {
  if (!ymd) return "Select Date";
  const [y, m, d] = String(ymd).split("-").map(Number);
  if (!y || !m || !d) return "Select Date";
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
function toEventDateDisplayLabel(ymd) {
  if (!ymd) return "";
  const [y, m, d] = String(ymd).split("-").map(Number);
  if (!y || !m || !d) return "";
  const month = new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short" });
  return `${d} ${month} ${y}`;
}
function priceForDayMap(dayMap, dateYmd) {
  if (!dayMap || typeof dayMap !== "object") return 0;
  const [y, m, d] = dateYmd.split("-").map(Number);
  if (!y || !m || !d) return 0;
  const weekday = DAY_NAMES[new Date(y, m - 1, d).getDay()];
  return Number(dayMap?.[weekday] || 0);
}

function parseTimeToMinutes(rawValue, fallback = 0) {
  const value = String(rawValue || "").trim().toLowerCase();
  if (!value) return fallback;
  const ampmMatch = /^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/.exec(value);
  if (ampmMatch) {
    let hour = Number(ampmMatch[1] || 0) % 12;
    const minute = Number(ampmMatch[2] || 0);
    if (ampmMatch[3] === "pm") hour += 12;
    return hour * 60 + minute;
  }
  const hhmmMatch = /^(\d{1,2})(?::(\d{1,2}))?$/.exec(value);
  if (hhmmMatch) {
    const hour = Number(hhmmMatch[1] || 0);
    const minute = Number(hhmmMatch[2] || 0);
    return hour * 60 + minute;
  }
  return fallback;
}

function normalizeSlotValue(rawValue) {
  const mins = parseTimeToMinutes(rawValue, -1);
  if (mins < 0) return "";
  return `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
}

function readBackendSlotAvailability(row) {
  if (!row || typeof row !== "object") return null;
  const boolCandidate = row.isAvailable ?? row.available;
  if (typeof boolCandidate === "boolean") return boolCandidate;
  const status = String(row.status || row.slotStatus || row.availability || "").toLowerCase();
  if (status === "available") return true;
  if (status === "unavailable" || status === "booked" || status === "full" || status === "soldout") return false;
  const capacityCandidate = row.availableCapacity ?? row.capacity ?? row.remaining;
  if (capacityCandidate != null && !Number.isNaN(Number(capacityCandidate))) return Number(capacityCandidate) > 0;
  return null;
}

export default function BookingPageComponent() {
  const { eventId = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTimeSlot, setSelectedTimeSlot] = useState("");
  const [guestCount, setGuestCount] = useState(0);
  const [maleGuests, setMaleGuests] = useState(0);
  const [femaleGuests, setFemaleGuests] = useState(0);
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitOk, setSubmitOk] = useState("");
  const [availabilityMeta, setAvailabilityMeta] = useState("");
  const [isDateAvailable, setIsDateAvailable] = useState(true);
  const [monthAvailableDates, setMonthAvailableDates] = useState({});
  const [monthCapacityByDate, setMonthCapacityByDate] = useState({});
  const [monthLoading, setMonthLoading] = useState(false);
  const [loadedMonthYm, setLoadedMonthYm] = useState("");
  const [slotAvailability, setSlotAvailability] = useState({});
  const [selectedAlcoholic, setSelectedAlcoholic] = useState([]);
  const [selectedNonAlcoholic, setSelectedNonAlcoholic] = useState([]);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getDetailEvent(eventId);
        if (!isApiSuccess(res.status) || !res.data) {
          setError(res.message || "Event not found");
          setEvent(null);
          return;
        }
        if (!cancelled) setEvent(res.data);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Error");
          setEvent(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const safeEvent = event || {};
  const name = safeEvent.name || safeEvent.title || "Event";
  const isTable = String(safeEvent.type || "").toLowerCase() === "table";
  const hostId = safeEvent?.hostId?._id || safeEvent?.hostId || safeEvent?.userId?._id || safeEvent?.userId || "";
  const rawEventType = safeEvent?.type || safeEvent?.category || safeEvent?.eventType || safeEvent?.eventCategory || "";
  const normalizedEventType = String(rawEventType).trim().toLowerCase();
  const isVipEntry = isVipEntryEvent(safeEvent);
  const isBoothOrVipEntry = normalizedEventType === "booth" || isVipEntry;
  const isBoothBooking = normalizedEventType === "booth";
  const isClubType = normalizedEventType === "club";
  const minGuest = Number(safeEvent?.minGuest ?? safeEvent?.minGuests ?? 0);
  const maxGuest = Number(safeEvent?.maxGuest ?? safeEvent?.maxGuests ?? 0);
  const totalGuests = isTable ? maleGuests + femaleGuests : guestCount;
  const defaultDetailBackTo =
    typeof location.state?.fromDetailPath === "string" && location.state.fromDetailPath.trim()
      ? location.state.fromDetailPath
      : `/event/${eventId}`;
  const detailBackTo =
    isBoothOrVipEntry && hostId
      ? `/club/${encodeURIComponent(String(hostId))}`
      : defaultDetailBackTo;
  const hideBackButton =
    isBoothBooking && String(location.state?.fromDetailPath || "").startsWith("/club/event/");
  const selectedTicketId = String(location.state?.ticketId || "").trim();
  const selectedTicket =
    location.state?.selectedTicket && typeof location.state.selectedTicket === "object"
      ? location.state.selectedTicket
      : null;
  const selectedBoothId = String(location.state?.boothId || "").trim();
  const selectedBooth =
    location.state?.selectedBooth && typeof location.state.selectedBooth === "object"
      ? location.state.selectedBooth
      : null;
  const checkDateIdParams = useMemo(
    () => ({
      ...(!isTable && selectedTicketId ? { ticketId: selectedTicketId } : {}),
      ...(selectedBoothId ? { boothId: selectedBoothId } : {}),
    }),
    [isTable, selectedTicketId, selectedBoothId]
  );
  const bookingMaxCapacity = useMemo(
    () =>
      resolveBookingMaxCapacity(
        safeEvent,
        selectedBooth,
        selectedBoothId,
        selectedTicket,
        selectedTicketId
      ),
    [safeEvent, selectedBooth, selectedBoothId, selectedTicket, selectedTicketId]
  );
  const activeBooth = useMemo(
    () =>
      resolveActiveBoothForBooking(safeEvent, {
        boothId: selectedBoothId,
        selectedBooth,
        isBoothBooking,
      }),
    [safeEvent, selectedBoothId, selectedBooth, isBoothBooking]
  );
  const floorLayoutUrl = useMemo(() => pickFloorLayoutUrl(activeBooth), [activeBooth]);
  const hasBoothBottleService = useMemo(() => isBoothBottleServiceEnabled(safeEvent), [safeEvent]);
  const boothBottleEventId = useMemo(() => getBottleServiceEventId(safeEvent), [safeEvent]);
  const bottleCatalog = useMemo(() => buildBottleCatalog(safeEvent), [safeEvent]);
  const alcoholicEntries = useMemo(() => toBottleEntries(bottleCatalog.alcoholic), [bottleCatalog.alcoholic]);
  const nonAlcoholicEntries = useMemo(
    () => toBottleEntries(bottleCatalog.nonAlcoholic),
    [bottleCatalog.nonAlcoholic]
  );
  const boothAlcoholicPayload = useMemo(
    () => buildBottleSelectionPayload(selectedAlcoholic, alcoholicEntries),
    [selectedAlcoholic, alcoholicEntries]
  );
  const boothNonAlcoholicPayload = useMemo(
    () => buildBottleSelectionPayload(selectedNonAlcoholic, nonAlcoholicEntries),
    [selectedNonAlcoholic, nonAlcoholicEntries]
  );
  const hasBoothBottleSelection =
    boothAlcoholicPayload.length > 0 || boothNonAlcoholicPayload.length > 0;
  const effectiveMaxGuest = bookingMaxCapacity || maxGuest;
  const todayYmd = toYmd(new Date());
  const eventStartDateYmd = useMemo(() => pickEventStartDateYmd(safeEvent), [safeEvent]);
  const eventEndDateYmd = useMemo(() => pickEventEndDateYmd(safeEvent), [safeEvent]);
  const fixedSingleEventDateYmd = useMemo(
    () => pickFixedSingleEventDateYmd(safeEvent),
    [safeEvent]
  );
  const isFixedSingleEventDate = Boolean(fixedSingleEventDateYmd);
  const fixedEventDateLabel = useMemo(
    () => toEventDateDisplayLabel(fixedSingleEventDateYmd),
    [fixedSingleEventDateYmd]
  );
  const bookingDate = fixedSingleEventDateYmd || selectedDate;
  const activeCalendarMonth = useMemo(() => {
    if (fixedSingleEventDateYmd) {
      const [y, m] = fixedSingleEventDateYmd.split("-").map(Number);
      if (y && m) return new Date(y, m - 1, 1);
    }
    return calendarMonth;
  }, [fixedSingleEventDateYmd, calendarMonth]);

  useEffect(() => {
    if (!fixedSingleEventDateYmd) return;
    setSelectedDate(fixedSingleEventDateYmd);
    setIsDateAvailable(true);
    setCheckError("");
    const [y, m] = fixedSingleEventDateYmd.split("-").map(Number);
    if (y && m) {
      setCalendarMonth(new Date(y, m - 1, 1));
    }
  }, [fixedSingleEventDateYmd]);

  const availableWeekDays = useMemo(() => {
    if (!isTable) return undefined;
    const male = safeEvent?.malePrice;
    const female = safeEvent?.femalePrice;
    const daySet = new Set();
    const collect = (obj) => {
      if (!obj || typeof obj !== "object" || Array.isArray(obj)) return;
      Object.keys(obj).forEach((k) => {
        const idx = DAY_NAMES.indexOf(String(k).toLowerCase());
        const val = Number(obj[k]);
        if (idx >= 0 && Number.isFinite(val)) daySet.add(idx);
      });
    };
    collect(male);
    collect(female);
    return daySet.size ? Array.from(daySet).sort((a, b) => a - b) : undefined;
  }, [isTable, safeEvent?.malePrice, safeEvent?.femalePrice]);

  const isDateAllowed = useCallback(
    (date) => {
      const ymd = toYmd(date);
      if (ymd < todayYmd) return false;
      if (eventStartDateYmd && ymd < eventStartDateYmd) return false;
      if (eventEndDateYmd && ymd > eventEndDateYmd) return false;
      if (Array.isArray(availableWeekDays) && availableWeekDays.length) return availableWeekDays.includes(date.getDay());
      return true;
    },
    [todayYmd, availableWeekDays, eventStartDateYmd, eventEndDateYmd]
  );

  useEffect(() => {
    if (fixedSingleEventDateYmd) return;
    const isBeforeStart = (ymd) => Boolean(eventStartDateYmd && ymd && ymd < eventStartDateYmd);
    const isAfterEnd = (ymd) => Boolean(eventEndDateYmd && ymd && ymd > eventEndDateYmd);
    const isOutOfBounds = (ymd) => isBeforeStart(ymd) || isAfterEnd(ymd);

    if (selectedDate && isOutOfBounds(selectedDate)) {
      setSelectedDate("");
      setSelectedTimeSlot("");
      setIsDateAvailable(true);
      setCheckError("");
    }
  }, [fixedSingleEventDateYmd, eventStartDateYmd, eventEndDateYmd, selectedDate]);

  const dateUnavailableOptions = useMemo(
    () => ({
      monthLoading,
      monthAvailableDates,
      monthCapacityByDate,
      bookingMaxCapacity,
      isDateAllowed,
      loadedMonthYm,
    }),
    [monthLoading, monthAvailableDates, monthCapacityByDate, bookingMaxCapacity, isDateAllowed, loadedMonthYm]
  );

  const calendarCells = useMemo(() => {
    const first = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
    const dayOffset = first.getDay();
    const lastDay = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < dayOffset; i += 1) cells.push(null);
    for (let d = 1; d <= lastDay; d += 1) cells.push(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [calendarMonth]);

  const timeSlots = useMemo(() => {
    const openRaw = String(safeEvent?.openingTime || safeEvent?.startTime || "21:00");
    const closeRaw = String(safeEvent?.closeTime || safeEvent?.endTime || "23:00");
    const format = (mins) => {
      const h24 = Math.floor(mins / 60) % 24;
      const m = mins % 60;
      const ampm = h24 >= 12 ? "PM" : "AM";
      const h12 = h24 % 12 || 12;
      return m === 0 ? `${h12} ${ampm}` : `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
    };
    let start = parseTimeToMinutes(openRaw, parseTimeToMinutes("21:00"));
    let end = parseTimeToMinutes(closeRaw, start + 120);
    if (end <= start) end = start + 180;
    const slots = [];
    while (start <= end) {
      slots.push({
        value: `${String(Math.floor(start / 60)).padStart(2, "0")}:${String(start % 60).padStart(2, "0")}`,
        label: format(start),
      });
      start += 60;
    }
    return slots.slice(0, 8);
  }, [safeEvent?.openingTime, safeEvent?.closeTime, safeEvent?.startTime, safeEvent?.endTime]);

  useEffect(() => {
    const next = {};
    timeSlots.forEach((s) => {
      next[s.value] = true;
    });
    setSlotAvailability(next);
  }, [timeSlots]);

  useEffect(() => {
    if (!isTable || !bookingDate || !eventId || !timeSlots.length) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await postCheckBookedDate({
          eventId,
          startDate: bookingDate,
          endDate: bookingDate,
          isTable: "yes",
          maleCount: maleGuests,
          femaleCount: femaleGuests,
          ...checkDateIdParams,
        });
        if (cancelled) return;
        if (!isApiSuccess(res?.status)) {
          setCheckError(res?.message || "Unable to check slot availability.");
          return;
        }
        const next = {};
        timeSlots.forEach((slot) => {
          next[slot.value] = true;
        });
        const rows = Array.isArray(res?.data) ? res.data : [];
        rows.forEach((row) => {
          const slotRaw =
            row?.timeSlot ||
            row?.slot ||
            row?.startTime ||
            row?.time ||
            row?.bookingTime ||
            row?.bookingSlot;
          const slotValue = normalizeSlotValue(slotRaw);
          if (!slotValue || !(slotValue in next)) return;
          const availability = readBackendSlotAvailability(row);
          if (availability != null) next[slotValue] = availability;
        });
        setSlotAvailability(next);
      } catch (e) {
        if (!cancelled) {
          setCheckError(e?.message || "Unable to check slot availability.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isTable, bookingDate, eventId, maleGuests, femaleGuests, timeSlots, selectedTicketId, selectedBoothId, isClubType, checkDateIdParams]);

  const isSelectedDateUnavailable = useMemo(() => {
    if (isTable || !bookingDate || monthLoading || isFixedSingleEventDate) return false;
    return isBookingDateUnavailable(bookingDate, { ...dateUnavailableOptions, monthLoading: false });
  }, [isTable, bookingDate, monthLoading, isFixedSingleEventDate, dateUnavailableOptions]);

  const daysCount = bookingDate ? 1 : 0;
  const startDateIso = bookingDate || "";
  const endDateIso = bookingDate || "";
  const invalidRangeMessage = isSelectedDateUnavailable ? INVALID_BOOKING_RANGE_MESSAGE : "";

  useEffect(() => {
    if (isTable || isFixedSingleEventDate || !bookingDate || monthLoading) {
      if (isFixedSingleEventDate) {
        setCheckError((prev) => (prev === INVALID_BOOKING_RANGE_MESSAGE ? "" : prev));
        setIsDateAvailable(true);
      }
      return;
    }
    if (isSelectedDateUnavailable) {
      setCheckError(INVALID_BOOKING_RANGE_MESSAGE);
      setIsDateAvailable(false);
    } else {
      setCheckError((prev) => (prev === INVALID_BOOKING_RANGE_MESSAGE ? "" : prev));
      setIsDateAvailable(true);
    }
  }, [isTable, isFixedSingleEventDate, bookingDate, monthLoading, isSelectedDateUnavailable]);

  const maleFemalePrice = useMemo(() => {
    if (!isTable || !bookingDate) return 0;
    let sum = 0;
    sum += priceForDayMap(safeEvent?.malePrice, bookingDate) * Number(maleGuests || 0);
    sum += priceForDayMap(safeEvent?.femalePrice, bookingDate) * Number(femaleGuests || 0);
    return Number(sum || 0);
  }, [isTable, bookingDate, safeEvent?.malePrice, safeEvent?.femalePrice, maleGuests, femaleGuests]);

  const validate = () => {
    if (!bookingDate) return "Please select date.";
    if (isTable && !selectedTimeSlot) return "Please select time slot.";
    if (isTable && selectedTimeSlot && slotAvailability[selectedTimeSlot] === false) {
      return "Selected time slot is unavailable. Please choose another slot.";
    }
    if (isTable && minGuest > 0 && totalGuests < minGuest) {
      return `Minimum ${minGuest} guests required (combined male and female). You selected ${totalGuests}.`;
    }
    if (isTable && maxGuest > 0 && totalGuests > maxGuest) {
      return `Maximum ${maxGuest} guests allowed (combined male and female). You selected ${totalGuests}.`;
    }
    if (invalidRangeMessage) return invalidRangeMessage;
    if (totalGuests < 1) return "Please add at least one guest.";
    if (!isTable && bookingDate) {
      const dateCap = capacityForBookingDate(
        bookingDate,
        monthCapacityByDate,
        monthLoading,
        bookingMaxCapacity
      );
      if (dateCap != null && dateCap > 0 && totalGuests > dateCap) {
        return `Maximum ${dateCap} members allowed for the selected date.`;
      }
    }
    if (!isTable && effectiveMaxGuest > 0 && totalGuests > effectiveMaxGuest) {
      return `Maximum ${effectiveMaxGuest} members allowed.`;
    }
    return "";
  };

  const handleCheckAvailability = async () => {
    setCheckError("");
    const v = validate();
    if (v) {
      setCheckError(v);
      return false;
    }
    setChecking(true);
    try {
      const res = await postCheckBookedDate({
        eventId,
        startDate: startDateIso,
        endDate: endDateIso,
        isTable: isTable ? "yes" : "no",
        maleCount: isTable ? maleGuests : 0,
        femaleCount: isTable ? femaleGuests : 0,
        timeSlot: isTable ? selectedTimeSlot : "",
        ...checkDateIdParams,
      });
      if (!isApiSuccess(res?.status)) {
        setCheckError(res?.message || "Selected date is unavailable.");
        setIsDateAvailable(false);
        return false;
      }
      if (!isTable && bookingDate) {
        const cap = capacityForBookingDate(
          bookingDate,
          Object.fromEntries(
            (Array.isArray(res?.data) ? res.data : []).map((row) => {
              const ymd = String(row?.date || "").split("T")[0];
              const rowCap = readDateCapacityFromRow(row);
              return ymd && rowCap != null ? [ymd, rowCap] : null;
            }).filter(Boolean)
          ),
          false,
          bookingMaxCapacity
        );
        if (cap != null && cap > 0) {
          setAvailabilityMeta(`Available capacity: ${cap}`);
        } else if (cap === 0) {
          setAvailabilityMeta("Sold out for selected date.");
        } else {
          setAvailabilityMeta("Date available.");
        }
      } else if (Array.isArray(res?.data) && res.data.length > 0) {
        const cap = res.data.reduce(
          (max, row) => Math.max(max, Number(readDateCapacityFromRow(row) ?? 0)),
          0
        );
        setAvailabilityMeta(isTable ? "" : `Available capacity: ${cap}`);
      } else {
        setAvailabilityMeta(isTable ? "" : bookingMaxCapacity > 0 ? `Available capacity: ${bookingMaxCapacity}` : "Date available.");
      }
      setIsDateAvailable(true);
      return true;
    } catch (e) {
      setCheckError(e?.message || "Failed to check availability.");
      setIsDateAvailable(false);
      return false;
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    if (!eventId) return;
    const loadMonthAvailability = async () => {
      const firstDay = new Date(activeCalendarMonth.getFullYear(), activeCalendarMonth.getMonth(), 1);
      const lastDay = new Date(activeCalendarMonth.getFullYear(), activeCalendarMonth.getMonth() + 1, 0);
      const monthYm = `${firstDay.getFullYear()}-${pad2(firstDay.getMonth() + 1)}`;
      setMonthLoading(true);
      try {
        const res = await postCheckBookedDate({
          eventId,
          startDate: toYmd(firstDay),
          endDate: toYmd(lastDay),
          isTable: isTable ? "yes" : "no",
          maleCount: isTable ? maleGuests : 0,
          femaleCount: isTable ? femaleGuests : 0,
          ...checkDateIdParams,
        });
        if (!isApiSuccess(res?.status)) {
          setMonthAvailableDates({});
          setMonthCapacityByDate({});
          setLoadedMonthYm("");
          return;
        }
        const next = {};
        const capMap = {};
        if (Array.isArray(res?.availableDates)) {
          res.availableDates.forEach((d) => {
            const ymd = String(d).split("T")[0];
            if (ymd) next[ymd] = true;
          });
        }
        if (Array.isArray(res?.data)) {
          res.data.forEach((row) => {
            const ymd = String(row?.date || "").split("T")[0];
            if (!ymd) return;
            const apiCap = readDateCapacityFromRow(row);
            const cap =
              apiCap != null && apiCap > 0
                ? apiCap
                : bookingMaxCapacity > 0
                  ? bookingMaxCapacity
                  : Number(apiCap ?? 0);
            capMap[ymd] = cap;
            if (cap > 0) next[ymd] = true;
          });
        }
        setMonthAvailableDates(next);
        setMonthCapacityByDate(capMap);
        setLoadedMonthYm(monthYm);
      } catch {
        setMonthAvailableDates({});
        setMonthCapacityByDate({});
        setLoadedMonthYm("");
      } finally {
        setMonthLoading(false);
      }
    };
    loadMonthAvailability();
  }, [
    activeCalendarMonth,
    eventId,
    isTable,
    guestCount,
    maleGuests,
    femaleGuests,
    selectedTicketId,
    selectedBoothId,
    isClubType,
    bookingMaxCapacity,
    checkDateIdParams,
  ]);

  const handleBookNow = async () => {
    setSubmitError("");
    setSubmitOk("");
    const available = await handleCheckAvailability();
    if (!available) return;
    setSubmitting(true);
    try {
      const reviewRes = hasBoothBottleService && hasBoothBottleSelection
        ? await postBoothBottleReviewSummary({
            eventid: eventId,
            bottleId: boothBottleEventId,
            members: totalGuests,
            days: daysCount,
            startDate: bookingDate,
            endDate: bookingDate,
            alcoholic: buildReviewBottlePayload(boothAlcoholicPayload),
            nonAlcoholic: buildReviewBottlePayload(boothNonAlcoholicPayload),
          })
        : await postReviewSummary({
            eventid: eventId,
            members: totalGuests,
            days: daysCount,
            startDate: toDmyShort(startDateIso),
            endDate: toDmyShort(endDateIso),
            maleFemalePrice: isTable ? maleFemalePrice : 0,
            ...(!isTable && selectedTicketId ? { ticketid: selectedTicketId } : {}),
            ...(isClubType && selectedBoothId ? { boothId: selectedBoothId } : {}),
          });
      if (!isApiSuccess(reviewRes?.status)) {
        setSubmitError(reviewRes?.message || "Failed to load review summary.");
        return;
      }
      navigate(`/booking/${eventId}/review`, {
        state: {
          event: safeEvent,
          bookingInput: {
            eventId,
            hostId,
            type: String(safeEvent?.type || "").trim(),
            selectedDate: bookingDate,
            selectedEndDate: bookingDate,
            timeSlot: selectedTimeSlot || null,
            guestCount: isTable ? 0 : guestCount,
            maleGuests: isTable ? maleGuests : 0,
            femaleGuests: isTable ? femaleGuests : 0,
            members: totalGuests,
            days: daysCount,
            maleFemalePrice: isTable ? maleFemalePrice : 0,
            isTable: isTable ? "yes" : "no",
            ...(!isTable && selectedTicketId
              ? {
                  ticketId: selectedTicketId,
                  selectedTicket,
                }
              : {}),
            ...(isClubType && selectedBoothId
              ? {
                  boothId: selectedBoothId,
                  selectedBooth,
                }
              : {}),
            ...(hasBoothBottleService && hasBoothBottleSelection
              ? {
                  bottleId: boothBottleEventId,
                  alcoholic: boothAlcoholicPayload,
                  nonAlcoholic: boothNonAlcoholicPayload,
                  hasBoothBottleService: true,
                }
              : {}),
          },
          reviewSummary: reviewRes,
        },
      });
    } catch (e) {
      setSubmitError(e?.message || "Failed to load review summary.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!eventId) {
    return (
      <WebsiteLayout>
        <div className="container py-4">
          <p style={{ color: colors.red }}>Missing event id.</p>
        </div>
      </WebsiteLayout>
    );
  }
  if (loading) {
    return (
      <WebsiteLayout>
        <LoadingSpinner label="Loading event…" />
      </WebsiteLayout>
    );
  }
  if (error || !event) {
    return (
      <WebsiteLayout>
        <div className="container py-4">
          <p style={{ color: colors.red }}>{error || "Not found"}</p>
          <Link to="/" style={{ color: colors.violate }}>
            Home
          </Link>
        </div>
      </WebsiteLayout>
    );
  }

  return (
    <WebsiteLayout>
      <div className="booking-page-shell">
        <div className="container py-4 py-lg-5 px-3 px-md-4 booking-page-inner">
          {!hideBackButton ? (
            <Link to={detailBackTo} className="booking-back-btn">
              <span aria-hidden>←</span> Event details
            </Link>
          ) : null}
          <h1 style={{ marginTop: 16, color: colors.white }}>Book · {name}</h1>
          <p style={{ color: colors.textColor }}>Type: {String(event.type)}</p>

          {floorLayoutUrl ? (
            <div className="booking-floor-layout">
              <p className="booking-floor-layout-label">Floor Layout</p>
              <div className="booking-floor-layout-image-wrap">
                <img
                  src={floorLayoutUrl}
                  alt="Floor layout"
                  className="booking-floor-layout-image"
                  loading="lazy"
                />
              </div>
            </div>
          ) : null}

          <div className="booking-card">
            <BookingCard
              safeEvent={safeEvent}
              selectedDate={bookingDate}
              calendarMonthLabel={toMonthLabel(calendarMonth)}
              onPrevMonth={() => setCalendarMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
              onNextMonth={() => setCalendarMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
              calendarCells={calendarCells}
              toYmd={toYmd}
              isDateAllowed={isDateAllowed}
              monthLoading={monthLoading}
              monthAvailableDates={monthAvailableDates}
              setSelectedDate={setSelectedDate}
              setCheckError={setCheckError}
              setAvailabilityMeta={setAvailabilityMeta}
              setIsDateAvailable={setIsDateAvailable}
              setSelectedTimeSlot={setSelectedTimeSlot}
              isTable={isTable}
              timeSlots={timeSlots}
              slotAvailability={slotAvailability}
              selectedTimeSlot={selectedTimeSlot}
              monthCapacityByDate={monthCapacityByDate}
              bookingMaxCapacity={bookingMaxCapacity}
              toShortDateLabel={toShortDateLabel}
              isFixedSingleEventDate={isFixedSingleEventDate}
              fixedEventDateLabel={fixedEventDateLabel}
            />

            {hasBoothBottleService ? (
              <BookingBottleSelection
                alcoholicEntries={alcoholicEntries}
                nonAlcoholicEntries={nonAlcoholicEntries}
                selectedAlcoholic={selectedAlcoholic}
                setSelectedAlcoholic={setSelectedAlcoholic}
                selectedNonAlcoholic={selectedNonAlcoholic}
                setSelectedNonAlcoholic={setSelectedNonAlcoholic}
                defaultReservationPrice={getDefaultReservationPrice(safeEvent)}
                selectedDate={bookingDate}
                disabled={submitting || checking}
              />
            ) : null}

            <BookingUserForm
              isTable={isTable}
              maleGuests={maleGuests}
              femaleGuests={femaleGuests}
              guestCount={guestCount}
              setMaleGuests={setMaleGuests}
              setFemaleGuests={setFemaleGuests}
              setGuestCount={setGuestCount}
              availabilityMeta={availabilityMeta}
              maxGuest={effectiveMaxGuest}
              minGuest={minGuest}
              submitting={submitting}
              checking={checking}
              selectedDate={bookingDate}
              isDateAvailable={isDateAvailable}
              onNext={handleBookNow}
            />

            {isTable ? (
              <div className="booking-actions">
                <button
                  type="button"
                  onClick={handleBookNow}
                  disabled={
                    submitting ||
                    checking ||
                    !bookingDate ||
                    !isDateAvailable ||
                    !selectedTimeSlot
                  }
                  className="booking-primary-btn"
                >
                  {submitting ? "Processing..." : "Next"}
                </button>
              </div>
            ) : null}

            <BookingCalculation
              isTable={isTable}
              availabilityMeta={availabilityMeta}
              checkError={checkError}
              invalidRangeMessage={invalidRangeMessage}
              submitError={submitError}
              submitOk={submitOk}
            />
          </div>
        </div>
      </div>
    </WebsiteLayout>
  );
}
